import {
  addUserWatchlistReducer,
  removeAllUserWatchlistReducer,
  removeUserWatchlistReducer,
} from "@store/metadata-slice";
import { deleteMethod, postMethod, putMethod } from "./rest-handler";
import { store } from "@store/index";
import { updateSelectedWatchlistReducer } from "@store/watchlist-slice";
import { isDesktop } from "@platform";

/**
 * Build the complete watchlists payload and push all watchlists to AmiBroker.
 * Mirrors the reference app's createWatchlistInAmibrokerHandler exactly:
 *   - feedsScrips → one .tls per feed segment (using scrip.symbol directly)
 *   - userWatchlist → one .tls per user watchlist (using scripIdMap lookup)
 *   - defaultWatchlist → one .tls per default watchlist (using scripIdMap lookup)
 *   - The user's default watchlist gets a " (Default)" suffix on its name
 */
export const createWatchlistInAmibrokerHandler = (dataToAmibroker: {
  feedsScrips: { id: string; name: string; scrips: { id: string; symbol: string }[] }[];
  userWatchlist: { id: string; name: string; scrips: string[] }[];
  defaultWatchlist: { id: string; name: string; scrips: string[] }[];
}): Promise<void> => {
  if (!isDesktop || !store.getState().amibroker?.isEnabled) return Promise.resolve();

  const watchlists: Record<string, string> = {};
  const scripIdMap: Record<string, string> =
    store.getState().metadata.feedsScripsState.symbolScripId as Record<string, string>;
  const userDefaultWatchlistId =
    store.getState().metadata.marketWatchState.userDefaultWatchlist.watchlistId;

  // Feed segments (e.g. NSE_EQ, NSE_FO) — each becomes a watchlist
  (dataToAmibroker.feedsScrips || []).forEach((feed) => {
    let scrips = "";
    (feed.scrips || []).forEach((scrip) => {
      scrips += scrip.symbol + "\n";
    });
    watchlists[feed.name] = scrips;
  });

  // User-created watchlists — scrips are IDs, resolved via scripIdMap
  (dataToAmibroker.userWatchlist || []).forEach((watchlist) => {
    let scrips = "";
    (watchlist.scrips || []).forEach((scripId) => {
      const symbol = scripIdMap[String(scripId)];
      if (symbol) scrips += symbol + "\n";
    });
    const watchlistName =
      watchlist.id === userDefaultWatchlistId
        ? `${watchlist.name} (Default)`
        : watchlist.name;
    watchlists[watchlistName] = scrips;
  });

  // System default watchlists — scrips are IDs, resolved via scripIdMap
  (dataToAmibroker.defaultWatchlist || []).forEach((watchlist) => {
    let scrips = "";
    (watchlist.scrips || []).forEach((scripId) => {
      const symbol = scripIdMap[String(scripId)];
      if (symbol) scrips += symbol + "\n";
    });
    const watchlistName =
      watchlist.id === userDefaultWatchlistId
        ? `${watchlist.name} (Default)`
        : watchlist.name;
    watchlists[watchlistName] = scrips;
  });

  return import("./ipc-handler").then(({ createWatchlistInAmibroker }) =>
    createWatchlistInAmibroker(watchlists)
  );
};

/** Sync a single newly-saved user watchlist to AmiBroker (fire-and-forget, desktop-only). */
const syncWatchlistCreate = (name: string, scripIds: string[]): void => {
  if (!isDesktop || !store.getState().amibroker?.isEnabled) return;
  const scripIdMap: Record<string, string> =
    store.getState().metadata.feedsScripsState.symbolScripId as Record<string, string>;
  let scrips = "";
  scripIds.forEach((id) => {
    const sym = scripIdMap[String(id)];
    if (sym) scrips += sym + "\n";
  });
  if (!scrips) return;
  import("./ipc-handler").then(({ createWatchlistInAmibroker }) => {
    createWatchlistInAmibroker({ [name]: scrips }).catch(console.error);
  });
};

/** Delete watchlist(s) from AmiBrokerI (fire-and-forget, desktop-only). */
const syncWatchlistDelete = (names: string[]): void => {
  if (!isDesktop || !store.getState().amibroker?.isEnabled || names.length === 0) return;
  import("./ipc-handler").then(({ deleteWatchlistInAmibroker }) => {
    deleteWatchlistInAmibroker(names).catch(console.error);
  });
};

/**
 * Create a new user watchlist.
 *
 * @param name   - Display name for the watchlist.
 * @param scrips - Array of numeric scrip IDs to include.
 */
export const saveWatchlist = (name: string, scrips: number[]) => {
  return new Promise<void>((resolve, reject) => {
    const url: string = "createUserWatchlist";
    const body: String = JSON.stringify({
      name,
      scrips,
    });
    postMethod(url, body)
      .then((response) => {
        const data = response["data"];
        const userWatchlist = {
          value: data.id,
          label: data.name,
          scrips: data.scrips,
        };
        store.dispatch(addUserWatchlistReducer(data));
        store.dispatch(updateSelectedWatchlistReducer(userWatchlist));
        // Sync newly created watchlist to AmiBrokerI if enabled
        syncWatchlistCreate(data.name, data.scrips || []);
        resolve();
      })
      .catch((error) => {
        reject(error);
      });
  });
};

/**
 * Set an existing watchlist as the user's default.
 *
 * @param watchlistId - ID of the watchlist to set as default.
 */
export const saveAsUserDefaultWatchlist = (watchlistId: string) => {
  return new Promise<void>((resolve, reject) => {
    const url: string = "updateUserDefaultWatchlist";
    const body: String = JSON.stringify({
      watchlistId,
    });
    putMethod(url, body)
      .then((response) => {
        resolve();
      })
      .catch((error) => {
        reject(error);
      });
  });
};

/**
 * Delete a user-created watchlist by ID.
 *
 * Clears the `selectedWatchlist` from Redux if the deleted watchlist was
 * currently selected.
 *
 * @param watchlistId - ID of the watchlist to delete.
 */
export const deleteUserWatchlist = (watchlistId: string) => {
  return new Promise<void>((resolve, reject) => {
    const url: string = "deleteUserWatchlist/" + watchlistId;
    deleteMethod(url)
      .then((response) => {
        // Look up watchlist name before removing from store
        const watchlistName = store
          .getState()
          .metadata.marketWatchState.userWatchlist
          .find((w) => w.id === watchlistId)?.name;
        if (watchlistName) syncWatchlistDelete([watchlistName]);
        store.dispatch(removeUserWatchlistReducer(watchlistId));
        store.dispatch(updateSelectedWatchlistReducer(null));
        resolve();
      })
      .catch((error) => {
        reject(error);
      });
  });
};

/**
 * Delete all user-created watchlists in a single API call.
 *
 * If the currently selected watchlist is a user-created one, the selection
 * is cleared from Redux.
 */
export const deleteAllUserWatchlist = () => {
  return new Promise<void>((resolve, reject) => {
    const url: string = "deleteAllUserWatchlist";
    deleteMethod(url)
      .then((response) => {
        // Collect all user watchlist names before clearing the store
        const allNames = store
          .getState()
          .metadata.marketWatchState.userWatchlist
          .map((w) => w.name);
        syncWatchlistDelete(allNames);

        const isUserWatchlistSelected =
          store
            .getState()
            .metadata.marketWatchState.userWatchlist.find(
              (option) =>
                option.id ===
                store.getState().watchlist.selectedWatchlist?.value
            ) !== undefined
            ? true
            : false;

        if (isUserWatchlistSelected) {
          store.dispatch(updateSelectedWatchlistReducer(null));
        }
        store.dispatch(removeAllUserWatchlistReducer());
        resolve();
      })
      .catch((error) => {
        reject(error);
      });
  });
};
